import { Size } from '@bms2/shared';
import { sizeClassMap } from '../classMaps';
import styles from './choice.module.css';

/**
 * Radio Button (piece 3): the native radio at the Checkbox's sizing, a navy
 * ring with a centered navy dot when checked, and the Checkbox's hover, focus
 * and disabled treatments. Exactly one radio is selected per named group.
 */
export interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  name: string;
  legend: string;
  options: readonly RadioOption[];
  value: string | null;
  onChange: (value: string) => void;
  size?: Size;
  disabled?: boolean;
  /** Horizontal lays the options in a row that wraps when the width is tight (product owner, 2026-09-10); vertical is the default. */
  orientation?: 'vertical' | 'horizontal';
  /** The legend is read by a screen reader and not drawn, where a table's head already names the choice (decision 2501). */
  legendHidden?: boolean;
}

export const radioDocumentedStates = ['unchecked', 'checked', 'disabled'] as const;

const sizeClass = sizeClassMap({ small: styles.small!, large: styles.large! });

export function RadioGroup({ name, legend, options, value, onChange, size = Size.Medium, disabled = false, orientation = 'vertical', legendHidden = false }: RadioGroupProps) {
  return (
    <fieldset className={`${styles.group} ${orientation === 'horizontal' ? styles.groupRow : ''}`} data-radio-group={name} data-orientation={orientation}>
      <legend className={legendHidden ? styles.hiddenLabel : styles.legend}>{legend}</legend>
      {options.map((option) => (
        <label key={option.value} className={styles.row} data-size={size}>
          <span className={`${styles.box} ${sizeClass[size]}`}>
            <input
              type="radio"
              className={`${styles.native} ${styles.radio}`}
              name={name}
              value={option.value}
              checked={value === option.value}
              disabled={disabled || option.disabled}
              onChange={() => onChange(option.value)}
            />
          </span>
          <span>{option.label}</span>
        </label>
      ))}
    </fieldset>
  );
}
